/**
 * <s-product-gallery> — PDP main media viewer with thumbnail navigation.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap track holding the slides.
 *   [data-gallery-slide]          — one slide per media, direct children of the track.
 *   button[data-gallery-thumb]    — thumbnail buttons; data-gallery-thumb holds the slide index.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    this.addEventListener('click', this.#handleThumbClick, { signal: this.#ac.signal });

    const slides = [...track.querySelectorAll('[data-gallery-slide]')];
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(slides.indexOf(entry.target));
        }
      },
      { root: track, threshold: 0.6 }
    );
    for (const slide of slides) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleThumbClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('button[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;

    const index = Number(thumb.dataset.galleryThumb);
    const track = this.querySelector('[data-gallery-track]');
    const slide = track?.querySelectorAll('[data-gallery-slide]')[index];
    if (!track || !slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const left = isRtl ? -(track.scrollWidth - slide.offsetLeft - slide.offsetWidth) : slide.offsetLeft;
    track.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  };

  #setActive(index) {
    if (index < 0) return;
    for (const thumb of this.querySelectorAll('button[data-gallery-thumb]')) {
      const active = Number(thumb.dataset.galleryThumb) === index;
      thumb.toggleAttribute('aria-current', active);
      if (active) thumb.setAttribute('aria-current', 'true');
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
